'use client';

import React, {createContext, useCallback, useEffect, useMemo, useState} from 'react';
import Theme from '@/theme';

type SkillzStyleContextType = {
  themeImagesUrl: string | null;
  sourceBaseUrl: string | null;
  baseThemeUrl: string | undefined;
  customThemeUrl: string | undefined;
  themeJSON: typeof Theme;
  gameIcon?: string | undefined;
  gameTitle: string;
  isThemeImage: (imageSrc: string) => boolean;
  isLocalImage: (imageSrc: string) => boolean;
  hasLoaded: boolean;
};

const SkillzStyleContext = createContext<SkillzStyleContextType>({
  themeImagesUrl: null,
  sourceBaseUrl: null,
  baseThemeUrl: undefined,
  customThemeUrl: undefined,
  themeJSON: Theme,
  gameIcon: undefined,
  gameTitle: '',
  isThemeImage: () => false,
  isLocalImage: () => false,
  hasLoaded: false,
});

interface SkillzStyleProviderProps {
  themeImagesUrl: string | null;
  sourceBaseUrl: string | null;
  baseThemeUrl: string | undefined;
  customThemeUrl: string | undefined;
  themeImages: string[];
  localImages: string[];
  gameIcon: string;
  gameTitle: string;
  themeJSON: typeof Theme;
  children: React.ReactNode;
}

export default function SkillzStyleProvider({
  children,
  themeImagesUrl,
  sourceBaseUrl,
  baseThemeUrl,
  customThemeUrl,
  themeImages,
  themeJSON,
  localImages,
  gameIcon,
  gameTitle,
}: SkillzStyleProviderProps) {
  const [hasLoaded, setHasLoaded] = useState(false);
  const localImagesSet = useMemo(() => new Set(localImages), [localImages]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.SkillzTheme = themeJSON;
      window.gameIcon = gameIcon;
      setHasLoaded(true);
    }
  }, [themeImagesUrl, themeJSON, gameIcon]);

  const isThemeImage = useCallback(
    (imageSrc: string) => {
      return themeImages.includes(imageSrc);
    },
    [themeImages]
  );

  const isLocalImage = useCallback(
    (imageSrc: string) => {
      return localImagesSet.has(imageSrc);
    },
    [localImagesSet]
  );

  return (
    <SkillzStyleContext.Provider
      value={{
        themeImagesUrl,
        themeJSON,
        isThemeImage,
        sourceBaseUrl,
        isLocalImage,
        baseThemeUrl,
        customThemeUrl,
        gameIcon,
        gameTitle,
        hasLoaded,
      }}
    >
      {hasLoaded && children}
    </SkillzStyleContext.Provider>
  );
}

const useSkillzStyle = () => {
  return React.useContext(SkillzStyleContext);
};

export {useSkillzStyle};
